import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import Quill from 'quill';
import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { DocumentService } from 'src/app/services/document.service';
import { LoadingService } from 'src/app/services/loading.service';
import { ToastService } from 'src/app/services/toast.service';
import { DocumentsComponent } from '../documents/documents.component';
import { DocumentPickerType, IDocumentItem, IDocumentPickerResult } from 'src/app/shared/interfaces/IDocument';
import { downloadEulaAttachment, ensureMime, getFilenameFromUrl, hydrateEulaMedia, persistEulaContent, preserveEmptyBlockBreaks, toDocumentHref } from './eula-content';
import { applyQuillToolbarTooltips, EULA_LINK_NO_UNDERLINE_CLASS } from './eula-quill.config';

interface AttachmentEdit {
  index: number;
  length: number;
  href: string;
}

@Injectable()
export class EulaQuillHost {
  private readonly objectUrlToDocumentUrl = new Map<string, string>();
  private quill: any;
  private savedSelection: { index: number; length: number } | null = null;
  private editingAttachment: AttachmentEdit | null = null;

  constructor(
    private dialog: MatDialog,
    private documentService: DocumentService,
    private loadingService: LoadingService,
    private toastService: ToastService,
    private translate: TranslateService) {}

  attach(quill: any) {
    const alreadyAttached = this.quill === quill;
    this.quill = quill;
    this.updateTooltips();
    if (alreadyAttached) return;
    this.setupToolbarHandlers();
    this.setupAttachLinkBehavior();
  }

  preview(html: string): string {
    return preserveEmptyBlockBreaks(this.quill?.root?.innerHTML || html || '');
  }

  persist(html: string): string {
    return persistEulaContent(this.quill?.getSemanticHTML?.() || html, this.objectUrlToDocumentUrl);
  }

  hydrate(html: string | null | undefined): Observable<string> {
    return hydrateEulaMedia(html ?? '', (path) => this.documentService.getDocumentBlob(path)).pipe(
      map(({ html: hydrated, mappings }) => {
        for (const { objectUrl, path } of mappings) {
          this.objectUrlToDocumentUrl.set(objectUrl, toDocumentHref(path));
        }
        return hydrated;
      })
    );
  }

  destroy() {
    this.objectUrlToDocumentUrl.forEach((_documentUrl, objectUrl) => URL.revokeObjectURL(objectUrl));
    this.objectUrlToDocumentUrl.clear();
  }

  onPreviewClick(event: MouseEvent) {
    downloadEulaAttachment(
      event,
      (path) => this.documentService.getDocumentBlob(path),
      () => this.toastService.error(this.translate.instant('error'))
    );
  }

  updateTooltips() {
    if (!this.quill) return;

    const toolbar = this.quill.getModule('toolbar');
    if (toolbar?.container) {
      applyQuillToolbarTooltips(toolbar.container, this.translate);
    }

    const label = (key: string, fallback: string) => {
      const translated = this.translate.instant(key);
      return translated && translated !== key ? translated : fallback;
    };
    const tooltipRoot = this.quill.theme?.tooltip?.root as HTMLElement | undefined;
    tooltipRoot?.style.setProperty('--eula-tooltip-file-label', JSON.stringify(`${label('file', 'File')}:`));
    tooltipRoot?.style.setProperty('--eula-tooltip-edit-label', JSON.stringify(label('edit', 'Edit')));
    tooltipRoot?.style.setProperty('--eula-tooltip-remove-label', JSON.stringify(label('remove', 'Remove')));

    const deleteLabel = label('delete', 'Delete');
    this.quill.container?.setAttribute('data-delete-label', deleteLabel);
    this.quill.container?.querySelectorAll('.eula-media-delete').forEach((el: Element) => {
      el.setAttribute('title', deleteLabel);
      el.setAttribute('aria-label', deleteLabel);
    });
  }

  private setupToolbarHandlers() {
    const toolbar = this.quill?.getModule('toolbar');
    if (!toolbar) return;
    toolbar.addHandler('link', () => this.openDocumentPicker('attach'));
    toolbar.addHandler('image', () => this.openDocumentPicker('image'));
    toolbar.addHandler('video', () => this.openDocumentPicker('video'));
    toolbar.addHandler('underline', (value: boolean) => {
      if (!this.toggleAttachUnderline()) this.quill.format('underline', value);
    });
  }

  private setupAttachLinkBehavior() {
    if (!this.quill) return;

    const tooltip = this.quill.theme?.tooltip;
    if (tooltip) {
      const originalShow = tooltip.show.bind(tooltip);
      tooltip.show = () => {
        this.syncAttachTooltipPreview(tooltip);
        originalShow();
      };

      const originalEdit = tooltip.edit.bind(tooltip);
      tooltip.edit = (mode?: string, preview?: string | null) => {
        if (mode && mode !== 'link') {
          originalEdit(mode, preview);
          return;
        }
        const edit = this.getAttachmentEditFromSelection();
        if (!edit) return;
        this.openDocumentPicker('attach', edit);
      };

      const originalPosition = tooltip.position.bind(tooltip);
      tooltip.position = (reference: { top: number; bottom: number; left: number; width: number }) => {
        const shift = originalPosition(reference);
        this.keepAttachTooltipInView(tooltip, reference);
        return shift;
      };

      tooltip.root?.querySelector('a.ql-preview')?.addEventListener('click', (event: Event) => {
        event.preventDefault();
      });

      tooltip.root?.querySelector('a.ql-remove')?.addEventListener('click', (event: Event) => {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.removeAttachment(tooltip);
      }, true);
    }

    this.quill.root.addEventListener('click', (event: MouseEvent) => {
      const anchor = (event.target as HTMLElement | null)?.closest?.('a') as HTMLAnchorElement | null;
      if (!anchor || !this.quill.root.contains(anchor)) return;
      event.preventDefault();
      if ((this.quill.getSelection()?.length ?? 0) > 0) return;
      const edit = this.getAttachmentEditFromAnchor(anchor);
      if (edit) this.showAttachTooltip(edit);
    });
  }

  private showAttachTooltip(edit: AttachmentEdit) {
    if (!this.quill) return;
    this.selectAttachment(edit.index, edit.length);

    const tooltip = this.quill.theme?.tooltip;
    if (!tooltip) return;

    tooltip.linkRange = { index: edit.index, length: edit.length };
    const previewEl = (tooltip.preview ?? tooltip.root?.querySelector('a.ql-preview')) as HTMLAnchorElement | null;
    previewEl?.setAttribute('href', edit.href);
    tooltip.show();
    const bounds = this.quill.getBounds(edit.index, edit.length);
    if (bounds) tooltip.position(bounds);
  }

  private keepAttachTooltipInView(tooltip: any, reference: { top: number; bottom: number }) {
    const el = tooltip?.root as HTMLElement | undefined;
    if (!el || el.classList.contains('ql-hidden')) return;

    const headerBottom = (document.querySelector('.topbar') as HTMLElement | null)?.getBoundingClientRect().bottom ?? 0;
    const minTop = headerBottom + 8;
    let rect = el.getBoundingClientRect();

    if (rect.top < minTop) {
      el.classList.remove('ql-flip');
      el.style.top = `${reference.bottom}px`;
      rect = el.getBoundingClientRect();
      if (rect.top < minTop) {
        const currentTop = parseFloat(el.style.top || '0') || 0;
        el.style.top = `${currentTop + (minTop - rect.top)}px`;
        rect = el.getBoundingClientRect();
      }
    }

    const sidebarRight = (document.querySelector('.mat-drawer.sidebarNav') as HTMLElement | null)?.getBoundingClientRect().right ?? 0;
    const minLeft = sidebarRight + 8;
    if (rect.left < minLeft) {
      const currentLeft = parseFloat(el.style.left || '0') || 0;
      el.style.left = `${currentLeft + (minLeft - rect.left)}px`;
    }
  }

  private syncAttachTooltipPreview(tooltip: any) {
    const previewEl = (tooltip.preview ?? tooltip.root?.querySelector('a.ql-preview')) as HTMLAnchorElement | null;
    if (!previewEl) return;

    const href = previewEl.getAttribute('href') || '';
    const range = tooltip.linkRange as { index: number; length: number } | undefined;
    const selectedText = range && this.quill ? this.quill.getText(range.index, range.length) : '';
    const fromUrl = getFilenameFromUrl(href);
    const text = (selectedText || previewEl.textContent || '').replace(/\u00a0/g, ' ').trim();
    const label = fromUrl || (text && text !== 'about:blank' ? text : '');
    if (!label) return;

    previewEl.textContent = label;
    previewEl.setAttribute('title', label);
  }

  private removeAttachment(tooltip: any) {
    if (!this.quill) return;
    const range = tooltip?.linkRange ?? this.getAttachmentEditFromSelection();
    if (!range) return;
    this.quill.formatText(range.index, range.length, 'link', false, 'user');
    if (tooltip) {
      delete tooltip.linkRange;
      tooltip.hide?.();
    }
  }

  private openDocumentPicker(type: DocumentPickerType, attachmentEdit?: AttachmentEdit | null) {
    this.savedSelection = this.captureSelection();
    this.editingAttachment = type === 'attach' ? (attachmentEdit ?? null) : null;
    this.quill?.theme?.tooltip?.hide?.();

    const dialogRef = this.dialog.open(DocumentsComponent, {
      width: '1200px',
      maxWidth: '95vw',
      height: '85vh',
      maxHeight: '90vh',
      data: { filterType: type }
    });

    dialogRef.afterClosed().subscribe((result: IDocumentPickerResult | undefined) => {
      if (!result?.file || !this.quill) {
        this.editingAttachment = null;
        return;
      }

      this.quill.focus();
      if (type === 'attach') {
        this.editingAttachment ? this.replaceAttachment(result.file, result.url) : this.insertAttachment(result.file, result.url);
        this.editingAttachment = null;
        return;
      }

      this.insertMediaEmbed(type, result.file, result.url);
    });
  }

  private getAttachmentEditFromAnchor(anchor: HTMLAnchorElement): AttachmentEdit | null {
    const blot = Quill.find(anchor) as any;
    if (!blot || !this.quill) return null;
    return {
      index: this.quill.getIndex(blot),
      length: blot.length(),
      href: anchor.getAttribute('href') || ''
    };
  }

  private getAttachmentEditFromSelection(): AttachmentEdit | null {
    if (!this.quill) return null;
    const range = this.quill.theme?.tooltip?.linkRange ?? this.savedSelection ?? this.captureSelection();
    const LinkBlot = Quill.import('formats/link');
    const [link, offset] = this.quill.scroll.descendant(LinkBlot, range.index);
    if (!link) return null;
    return {
      index: range.index - (offset ?? 0),
      length: link.length(),
      href: link.domNode?.getAttribute('href') || ''
    };
  }

  private insertAttachment(file: IDocumentItem, fileUrl: string) {
    const range = this.savedSelection ?? this.captureSelection();
    if (range.length > 0) {
      this.quill.formatText(range.index, range.length, 'link', fileUrl, 'user');
      this.selectAttachment(range.index, range.length);
      return;
    }

    this.quill.insertText(range.index, file.name, 'link', fileUrl, 'user');
    this.selectAttachment(range.index, file.name.length);
  }

  private replaceAttachment(file: IDocumentItem, fileUrl: string) {
    const edit = this.editingAttachment;
    if (!edit || !this.quill) return;

    const currentText = this.quill.getText(edit.index, edit.length).replace(/\u00a0/g, ' ').trim();
    if (getFilenameFromUrl(edit.href) === currentText) {
      this.quill.deleteText(edit.index, edit.length, 'user');
      this.quill.insertText(edit.index, file.name, 'link', fileUrl, 'user');
      this.selectAttachment(edit.index, file.name.length);
      return;
    }

    this.quill.formatText(edit.index, edit.length, 'link', fileUrl, 'user');
    this.selectAttachment(edit.index, edit.length);
  }

  private selectAttachment(index: number, length: number) {
    this.quill.setSelection(index, length, 'user');
  }

  private toggleAttachUnderline(): boolean {
    const edit = this.getAttachmentEditFromSelection();
    if (!edit) return false;

    const LinkBlot = Quill.import('formats/link');
    const [link] = this.quill.scroll.descendant(LinkBlot, edit.index);
    const anchor = link?.domNode as HTMLElement | undefined;
    if (!anchor || anchor.tagName !== 'A') return false;

    const href = anchor.getAttribute('href') || '';
    const noUnderline = !anchor.classList.contains(EULA_LINK_NO_UNDERLINE_CLASS);
    this.quill.formatText(edit.index, edit.length, 'link', { href, noUnderline }, 'user');
    return true;
  }

  private insertMediaEmbed(type: DocumentPickerType, file: IDocumentItem, fileUrl: string) {
    if (!file.path) return;

    this.loadingService.show();
    this.documentService.getDocumentBlob(file.path).pipe(
      switchMap((blob) => this.toEditorSrc(blob, file, fileUrl))
    ).subscribe({
      next: (src) => {
        this.loadingService.hide();
        this.quill.focus();
        const range = this.savedSelection ?? this.captureSelection();
        if (range.length > 0) {
          this.quill.deleteText(range.index, range.length, 'user');
        }
        this.quill.insertEmbed(range.index, type === 'video' ? 'video' : 'image', src, 'user');
        const editorWidth = this.quill.root?.clientWidth || 600;
        this.quill.formatText(range.index, 1, 'width', String(Math.min(280, Math.round(editorWidth * 0.4))), 'user');
        this.quill.setSelection(range.index + 1, 0, 'user');
      },
      error: () => {
        this.loadingService.hide();
        this.toastService.error(this.translate.instant('error'));
      }
    });
  }

  private captureSelection(): { index: number; length: number } {
    const selection = this.quill?.getSelection() || this.quill?.selection?.savedRange || null;
    const length = this.quill?.getLength() ?? 1;
    const index = Math.max(0, Math.min(selection?.index ?? length - 1, length - 1));
    return { index, length: selection?.length ?? 0 };
  }

  private toEditorSrc(blob: Blob, file: IDocumentItem, documentUrl: string): Observable<string> {
    const objectUrl = URL.createObjectURL(ensureMime(blob, file));
    this.objectUrlToDocumentUrl.set(objectUrl, documentUrl);
    return of(objectUrl);
  }
}
